import * as React from "react";
import { observer } from "mobx-react";

import { UitkLink } from "uitk-react-link";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkCard, UitkCardContent, UitkCardTextSegment } from "uitk-react-cards";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withStores } from "stores";
import { UitkExpandoCard, UitkExpandoCardTypeSize } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import JumplinksTitle from "./components/JumplinksTitle";
import TravelGuideLink from "./components/TravelGuideLink";
import { getJumplinkLocalizedNames, getJumplinkTitleString } from "./JumplinksUtils";

import { JumplinksProps, JumplinkItem, JumplinksFTNodeOptions } from "./typings";
import { normalizeOptionsData } from "src/stores/FlexTemplateStore";

const Jumplinks = withStores(
  "analytics",
  "context",
  "dx",
  "flexModuleModelStore"
)(
  observer((props: JumplinksProps) => {
    const { flexTemplateNode, flexModuleModelStore, context, dx } = props;
    const { id } = flexTemplateNode;
    const model = normalizeOptionsData(flexTemplateNode, flexModuleModelStore.getModel(id)) as JumplinksFTNodeOptions;
    const { jumplinksArray } = dx;

    const keyHelper = new ItemKeyHelper("jumplinks");
    const [isVisible, setIsVisible] = React.useState(false);
    const toggle = React.useCallback(() => setIsVisible(!isVisible), [isVisible]);

    // Main logic in the following is covered in test, only branch coverage is lacking
    /* istanbul ignore next */
    React.useEffect(() => {
      const scrollOnPageLoad = (): void => {
        const jumplinkPathName = location.hash.replace("#", "");

        const associatedJumplink = jumplinksArray.find(
          (jumplink: JumplinkItem) => jumplink.pathName === jumplinkPathName
        );

        if (!associatedJumplink) {
          return;
        }

        const element = document.getElementById(associatedJumplink.id);
        if (!element) {
          return;
        }

        scrollTo(element);
      };

      window.addEventListener("load", scrollOnPageLoad);

      return () => window.removeEventListener("load", scrollOnPageLoad);
    });

    const handleClick = (jumpLinkId: string) => (): void => {
      const element = document.getElementById(jumpLinkId);
      if (!element) {
        return;
      }

      scrollTo(element);
    };

    /* istanbul ignore next */
    const scrollTo = (elementToScrollTo: HTMLElement): void => {
      let wizardHeight = 0;

      const { deviceInformation } = context;
      const isMobile = deviceInformation?.mobile;

      if (!isMobile) {
        const wizardElement = document.getElementById("wizard-hotel-pwa-1");

        if (wizardElement) {
          wizardHeight = wizardElement.offsetHeight;
        }
      }

      const offsetPosition = elementToScrollTo.offsetTop;

      getFirstNavElement(elementToScrollTo).focus({ preventScroll: true });

      if (offsetPosition) {
        window.scrollTo({
          top: offsetPosition - wizardHeight,
          behavior: "smooth",
        });
      } else {
        elementToScrollTo.scrollIntoView();
      }
    };

    /* istanbul ignore next */
    const getFirstNavElement = (parentElement: HTMLElement): HTMLElement => {
      if (parentElement.getElementsByTagName("a").length) {
        return parentElement.getElementsByTagName("a")[0];
      }

      if (parentElement.getElementsByTagName("button").length) {
        return parentElement.getElementsByTagName("button")[0];
      }

      return parentElement;
    };

    const getAnalytics = (jumplinkName: string): AnalyticsConfig => ({
      id: `TG.LP.Dest.JumpTo.jumplinks.${jumplinkName}`,
      callback: (moduleId: string, description: string) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.CLICK,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
      },
    });

    const jumplLinkItems = (jumplinkItemsArray: JumplinkItem[]) =>
      jumplinkItemsArray.map((jumplink: JumplinkItem) => (
        <UitkLink
          key={keyHelper.next()}
          onClick={handleClick(jumplink.id)}
          analytics={getAnalytics(jumplink.pathName)}
          aria-label={jumplink.pathName}
        >
          <a tabIndex={0} href={`#${jumplink.pathName}`}>
            {getJumplinkLocalizedNames(jumplink)}
          </a>
        </UitkLink>
      ));

    const cardView = (isMobile: boolean) => (
      <UitkCard className="uitk-flat-border-top">
        <UitkCardContent>
          <UitkCardTextSegment>
            {!isMobile && <JumplinksTitle model={model} context={context} />}
            {jumplLinkItems(jumplinksArray)}
          </UitkCardTextSegment>
          <TravelGuideLink model={model} context={context} />
        </UitkCardContent>
      </UitkCard>
    );

    const expandoView = (isMobile: boolean) => (
      <UitkSpacing margin={{ blockstart: "two" }}>
        <UitkExpandoCard
          isVisible={isVisible}
          handleClick={toggle}
          expandoTitle={getJumplinkTitleString(model, context)}
          expandoDescription={cardView(isMobile)}
          expandoTitleSize={isMobile ? UitkExpandoCardTypeSize.TYPE_400 : UitkExpandoCardTypeSize.TYPE_500}
        />
      </UitkSpacing>
    );

    return (
      <div id={id} className="uitk-cell all-cell-1-1 Jumplinks" data-testid="jumplinks">
        <Viewport>
          <ViewMedium>{cardView(false)}</ViewMedium>
          <ViewSmall>{expandoView(true)}</ViewSmall>
        </Viewport>
      </div>
    );
  })
);

export default Jumplinks;
